/**
 * Grouping startups into board columns.
 *
 * A pure function so the grouping is testable without a DOM: an empty column
 * must still exist (a stage with no startups is information, not absence), and
 * the column order must follow the funnel rather than whatever order the API
 * returned rows in.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.1
 */
import type { StartupSummary } from '@nexus/contracts';

import { STAGES, type Stage } from '../../components/domain/stages';

/** One column: a stage and the startups in it. */
export interface BoardGroup {
  stage: Stage;
  startups: StartupSummary[];
}

/**
 * Groups startups by stage, in funnel order.
 *
 * Every stage gets a column even when empty — "nothing in Qualified" is a fact
 * worth seeing, and a board whose columns appear and disappear as records move
 * is disorienting.
 */
export function groupByStage(startups: readonly StartupSummary[]): BoardGroup[] {
  const groups = new Map<Stage, StartupSummary[]>(STAGES.map((stage) => [stage, []]));

  for (const startup of startups) {
    /* A stage the UI does not know is dropped rather than crashing the board:
       the contract guard makes that a compile error, so this only fires if the
       API is ahead of a deployed bundle. */
    groups.get(startup.stage)?.push(startup);
  }

  /* Non-null asserted rather than defaulted: the map was seeded with every
     stage above, so a `?? []` would be an unreachable branch. */
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see above
  return STAGES.map((stage) => ({ stage, startups: groups.get(stage)! }));
}

/** True when the id names one of the nine stages. */
export function isStage(value: unknown): value is Stage {
  return typeof value === 'string' && (STAGES as readonly string[]).includes(value);
}
